import { useEffect, useMemo, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { ExternalLink, Download, X } from 'lucide-react';
import { useInstallPrompt } from '../../hooks/useInstallPrompt';
import { androidIntentUrl, detectBrowserContext } from '../../utils/in-app-browser';
import {
  trackInstallPromptAccepted,
  trackOpenInAppShown,
  trackOpenInAppTapped,
} from '../../utils/analytics';
import { log } from '../../utils/logger';
import './OpenInAppBanner.css';

/** Per-tab: leaving the WebView is a one-shot decision, not a lasting preference. */
const ESCAPE_DISMISSED = 'draughts-open-in-app-dismissed';
/** Persistent: an install nudge that comes back every visit is nagging. */
const INSTALL_DISMISSED = 'draughts-install-nudge-dismissed';

function readFlag(store: Storage | undefined, key: string): boolean {
  try {
    return store?.getItem(key) === '1';
  } catch {
    return false; // Private mode / storage blocked — show it, don't crash.
  }
}

function writeFlag(store: Storage | undefined, key: string) {
  try {
    store?.setItem(key, '1');
  } catch {
    // Nothing to do: the banner just reappears next time.
  }
}

interface OpenInAppBannerProps {
  /**
   * The URL this visit ARRIVED on, captured on first render.
   *
   * Deliberately not `location.href`: App.tsx strips the deep-link query off the
   * address bar on mount, so by the time this renders the live URL is a bare `/`
   * and reopening it would land on the home page instead of the shared challenge —
   * which is indistinguishable from the bug this banner exists to fix.
   */
  arrivalUrl: string;
  /** Whether that URL actually carried a shared payload. */
  active: boolean;
}

/**
 * "Open in the app" / "Install the app", for someone who followed a shared link
 * (see utils/share.ts) and landed outside the installed PWA.
 *
 * Why it has to exist: WhatsApp — the way these links actually travel — renders
 * them in its own embedded WebView, which never consults Android's intent
 * resolver, so an installed WebAPK is never offered the URL no matter what the
 * manifest declares. The only fix is an explicit escape the visitor taps.
 *
 * On iOS there is no escape at all (home-screen web apps cannot capture links, and
 * there is no intent: scheme), so the iOS copy points at the system browser and
 * does NOT promise the app.
 */
export default function OpenInAppBanner({ arrivalUrl, active }: OpenInAppBannerProps) {
  const { t } = useTranslation();
  const { installable, promptInstall } = useInstallPrompt();
  const [dismissed, setDismissed] = useState(false);
  const [copied, setCopied] = useState(false);

  // Read once: the UA can't change mid-session, and detectBrowserContext consults
  // matchMedia, which is not free to re-run on every render.
  const context = useMemo(() => detectBrowserContext(), []);

  const isEscape = context === 'in-app-android' || context === 'in-app-ios';
  const isInstallNudge = context === 'browser' && installable;

  const alreadyDismissed = isEscape
    ? readFlag(typeof window === 'undefined' ? undefined : window.sessionStorage, ESCAPE_DISMISSED)
    : readFlag(typeof window === 'undefined' ? undefined : window.localStorage, INSTALL_DISMISSED);

  const visible = active && !dismissed && !alreadyDismissed && (isEscape || isInstallNudge);

  useEffect(() => {
    if (visible) trackOpenInAppShown(context);
  }, [visible, context]);

  if (!visible) return null;

  const dismiss = () => {
    setDismissed(true);
    writeFlag(
      isEscape ? window.sessionStorage : window.localStorage,
      isEscape ? ESCAPE_DISMISSED : INSTALL_DISMISSED
    );
  };

  const handleInstall = async () => {
    trackOpenInAppTapped(context);
    const accepted = await promptInstall();
    if (accepted) trackInstallPromptAccepted();
    setDismissed(true);
  };

  const handleCopy = async () => {
    trackOpenInAppTapped(context);
    try {
      await navigator.clipboard.writeText(arrivalUrl);
      setCopied(true);
      setTimeout(() => setCopied(false), 2000);
    } catch (err) {
      log.warn('OpenInAppBanner', 'Clipboard write failed', { err });
    }
  };

  return (
    <div className="open-in-app-banner" role="status">
      <div className="oiab-card">
        <div className="oiab-row">
          <span className="oiab-icon" aria-hidden="true">
            {isInstallNudge ? <Download size={18} /> : <ExternalLink size={18} />}
          </span>
          <p className="oiab-text">
            {isInstallNudge
              ? t('pwa.installText')
              : context === 'in-app-android'
                ? t('pwa.openInAppText')
                : t('pwa.openInBrowserText')}
          </p>
          <button
            type="button"
            className="oiab-close"
            onClick={dismiss}
            aria-label={t('pwa.dismiss')}
          >
            <X size={18} />
          </button>
        </div>

        {/* iOS gets no action button that could open the app, because none exists.
            Copying the link is the only thing that actually helps: it survives the
            trip to Safari, which the visitor has to make through WhatsApp's own
            menu. */}
        {context === 'in-app-ios' ? (
          <>
            <p className="oiab-hint">{t('pwa.iosHint')}</p>
            <button type="button" className="oiab-action oiab-action-secondary" onClick={handleCopy}>
              {copied ? t('pwa.linkCopied') : t('pwa.copyLink')}
            </button>
          </>
        ) : isInstallNudge ? (
          <button type="button" className="oiab-action" onClick={handleInstall}>
            {t('pwa.installCta')}
          </button>
        ) : (
          /* A real anchor, not a scripted redirect: WebViews routinely swallow
             programmatic navigation to intent:, and an unannounced jump out of the
             app would be hostile to someone who hasn't installed anything. */
          <a
            className="oiab-action"
            href={androidIntentUrl(arrivalUrl)}
            onClick={() => trackOpenInAppTapped(context)}
          >
            {t('pwa.openInAppCta')}
          </a>
        )}
      </div>
    </div>
  );
}
